/* oxlint-disable max-lines */
import classNames from 'clsx';
import RBush from 'rbush';
import { IJsonQuery } from 'shared/common/packages/api-models/common/json_query';
import { commarize } from 'shared/common/utils/number';
import {
  BALLOON_ACTIVE_Z_INDEX,
  BALLOON_HOVERED_Z_INDEX,
  BALLOON_PROMO_Z_INDEX,
} from 'shared/map-search/constants/map';

import * as styles from './styles.css';
import { formatBalloonCount, sortPins } from './utils';
import { IRBushItem, TFeaturePropertiesRequiredPrice, TFeature, IFeatureProperties } from '../../../types/map';
import { geoJsonFeatureToRBushItem } from '../../features';
import { trackPromoPinsLoad } from '../../tracking';
import { getIcon, getIconV2 } from '../BalloonManager/utils';

interface INewbuildingsPromoPinsManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
  isRedesign: boolean;
}

export function defineNewbuildingsPromoPinsManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('NewbuildingsPromoPinsManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'NewbuildingsPromoPinsManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class NewbuildingsPromoPinsManager {
        public readonly objectManager: YMaps.ObjectManager;

        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        private selectedFeature: TFeature | null;
        private viewedNewbuildings: string[];
        private currentNewbuildings: IRBushItem[];
        private randomizeNextStep: boolean;
        private sendShowAnalytics: boolean;
        private isClustersRedesign: boolean;
        private isRedesign: boolean;

        public constructor(options: INewbuildingsPromoPinsManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.selectedFeature = null;
          this.viewedNewbuildings = [];
          this.currentNewbuildings = [];
          this.randomizeNextStep = true;
          this.sendShowAnalytics = true;
          this.objectManager = new ymaps.ObjectManager();
          this.isRedesign = options.isRedesign;
        }

        public setShowAnalyticsState = (value: boolean) => {
          this.sendShowAnalytics = value;
        };

        public setIsClustersRedesign = (value: boolean) => {
          this.isClustersRedesign = value;
        };

        public setSelectedFeature = (clickedFeature: TFeature | null) => {
          const feature: TFeature | null = clickedFeature
            ? this.objectManager.objects.getById(clickedFeature.properties.featureId || clickedFeature.id)
            : null;

          if (feature) {
            this.updateBalloonProperties(feature.id, {
              ...feature.properties,
              isActive: true,
            });

            if (this.isClustersRedesign || this.isRedesign) {
              this.updatePinProperties(`nbpin_${feature.id}`, {
                ...feature.properties,
                isActive: true,
              });
            }
          }

          if (this.selectedFeature && feature?.id !== this.selectedFeature?.id) {
            this.updateBalloonProperties(this.selectedFeature.id, {
              ...this.selectedFeature.properties,
              isActive: false,
            });

            if (this.isClustersRedesign || this.isRedesign) {
              this.updatePinProperties(`nbpin_${this.selectedFeature.id}`, {
                ...this.selectedFeature.properties,
                isActive: false,
              });
            }
          }

          this.sendShowAnalytics = false;
          this.selectedFeature = feature;
        };

        public clear = () => {
          this.rbush.clear();
          this.objectManager.removeAll();
          this.viewedNewbuildings = [];
          this.currentNewbuildings = [];
          this.randomizeNextStep = true;
        };

        public loadNewbuildings = ({
          features,
          jsonQuery,
          promoPinsDisplayLimit,
        }: {
          features: TFeature[];
          jsonQuery: IJsonQuery;
          promoPinsDisplayLimit: number;
        }) => {
          this.sendShowAnalytics = true;
          this.clear();
          this.rbush.load(features.map(geoJsonFeatureToRBushItem));
          this.randomizeNextStep = true;
          setTimeout(
            () =>
              this.drawPinsWithBalloons({
                forceRandomize: false,
                jsonQuery,
                promoPinsDisplayLimit,
              }),
            10,
          );
        };

        public drawPinsWithBalloons = ({
          forceRandomize = false,
          jsonQuery,
          promoPinsDisplayLimit,
        }: {
          forceRandomize?: boolean;
          jsonQuery: IJsonQuery;
          promoPinsDisplayLimit: number;
        }) => {
          this.objectManager.removeAll();
          const randomize = forceRandomize || this.randomizeNextStep;

          const bounds = this.map.getBounds();
          const rBushItems = randomize
            ? this.rbush
                .search({
                  minX: bounds[0][0],
                  minY: bounds[0][1],
                  maxX: bounds[1][0],
                  maxY: bounds[1][1],
                })
                .sort(() => Math.random() - 0.5)
                .sort((a, b) => sortPins(a, b, this.viewedNewbuildings, this.selectedFeature))
                .slice(0, promoPinsDisplayLimit)
            : this.currentNewbuildings;

          this.viewedNewbuildings = rBushItems.map(el => el.feature.id);
          this.currentNewbuildings = rBushItems;
          this.randomizeNextStep = false;

          this.objectManager.add({
            type: 'FeatureCollection',
            features: [
              ...(rBushItems.map(this.prepareBalloon).filter(feature => !!feature) as TFeature[]),
              ...(rBushItems.map(this.preparePin).filter(feature => !!feature) as TFeature[]),
            ],
          });

          if (this.sendShowAnalytics) {
            trackPromoPinsLoad({
              pins: rBushItems,
              zoom: Math.round(this.map.getZoom()),
              jsonQuery,
            });

            this.sendShowAnalytics = false;
          }
        };

        public updateBalloonProperties = (featureId: string, properties: IFeatureProperties) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties),
            zIndex: this.getInteractionZIndex(properties, BALLOON_PROMO_Z_INDEX),
          });
        };

        public updatePinProperties = (featureId: string, properties: IFeatureProperties) => {
          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.preparePinLayout(properties),
            zIndex: this.getInteractionZIndex(properties, 1),
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem) => {
          const { properties } = feature;

          if (!properties.minPrice) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties as TFeaturePropertiesRequiredPrice),
              zIndex: this.getInteractionZIndex(properties, BALLOON_PROMO_Z_INDEX),
            },
          };
        };

        private preparePin = ({ feature }: IRBushItem) => {
          return {
            ...feature,
            id: `nbpin_${feature.id}`,
            options: {
              iconLayout: this.preparePinLayout(feature.properties),
              zIndex: this.getInteractionZIndex(feature.properties, 1),
            },
          };
        };

        private getInteractionZIndex = (properties: IFeatureProperties, defaultZIndex: number) => {
          if (properties.isActive) {
            return BALLOON_ACTIVE_Z_INDEX;
          }

          return properties.isHovered ? BALLOON_HOVERED_Z_INDEX : defaultZIndex;
        };

        private prepareBalloonLayout = (properties: IFeatureProperties) => {
          const content = this.prepareBalloonContent(properties);
          const isClustersRedesign = this.isClustersRedesign;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              // oxlint-disable-next-line typescript/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const wrapperElement = element.firstElementChild;
              if (!wrapperElement) {
                return null;
              }

              const boundingRect = wrapperElement.getBoundingClientRect();
              const offsetX = -boundingRect.width / 2;

              const shiftY = isClustersRedesign ? 6 : 4;
              const offsetY = -boundingRect.height - shiftY;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + boundingRect.width, offsetY + boundingRect.height],
                ]),
              );
            },
          });
        };

        private preparePinLayout = (properties: IFeatureProperties) => {
          const isClustersRedesign = this.isClustersRedesign;

          const { isHovered, isActive } = properties;

          const content = `<div class="${classNames(styles['point'], {
            [styles['point--redesign']]: isClustersRedesign,
            [styles['point--redesign-v2']]: this.isRedesign,
            [styles['point--hovered']]: isHovered && !isActive,
            [styles['point--active']]: isActive,
          })}"></div>`;

          return templateLayoutFactory.createClass(content, {
            getShape() {
              return new shapeRectangle(new geometryPixelRectangle([[0, 0], isClustersRedesign ? [10, 10] : [12, 12]]));
            },
          });
        };

        private prepareBalloonContent(feature: IFeatureProperties) {
          const { count, minPrice, isActive, newbuilding, isFavorite, hasDiscountOffers, isHovered } = feature;
          const hasActiveStyles = !!isActive || (!!isHovered && (this.isClustersRedesign || this.isRedesign));

          const newbuildingId = newbuilding?.id ?? 0;
          const price = minPrice ? `${count > 1 ? 'от ' : ''}${commarize(minPrice)}` : '';
          const countMarkup =
            count > 1
              ? `<div class="${styles['count']}">${formatBalloonCount(count, this.isClustersRedesign ? 99 : undefined)}</div>`
              : '';

          const icon = this.isRedesign
            ? getIconV2({
                isFavorite,
                isPromo: hasDiscountOffers,
                isActive: hasActiveStyles,
              })
            : getIcon({
                isFavorite,
                isPromo: hasDiscountOffers,
                isActive: hasActiveStyles,
                isRedesign: this.isClustersRedesign,
              });

          return `
            <div data-testid="promo-pin" data-newbuilding="${newbuildingId}" class="${classNames(styles['wrapper'], {
              [styles['wrapper--redesign']]: this.isClustersRedesign || this.isRedesign,
            })}">
              <div class="${classNames(styles['balloon'], isActive && styles['active'], {
                [styles['balloon--redesign']]: this.isClustersRedesign,
                [styles['balloon--redesign-v2']]: this.isRedesign,
                [styles['balloon--hovered']]: isHovered && !isActive,
              })}">
                ${countMarkup}
                <div class="${styles['price']}">${price}</div>
                <div class="${styles['icon']}">${icon}</div>
              </div>
            </div>
          `;
        }
      }

      provide(NewbuildingsPromoPinsManager);
    },
  );
}
